import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { TabPanel, TabView } from "primereact/tabview";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import Input from "../../componentes/input/input";
import { Loading } from "../../componentes/loading/loading";
import { useAtribuicoesConfig } from "../../hooks/solicitacoes/useAtribuicoesConfig";
import { useEtapasConfiguracao } from "../../hooks/solicitacoes/useEtapasConfiguracao";
import { PROCESSO_REGISTRY, processoInfo } from "../../utils/solicitacoes/processoRegistry";
import FilialAprovacaoConfigTab from "./FilialAprovacaoConfigTab";
import ConfiguracaoGeralTab from "./ConfiguracaoGeralTab";

const TIPO_ATRIBUICAO_OPTIONS = [
    { value: "USUARIO", label: "Usuário" },
    { value: "GRUPO", label: "Grupo" },
    { value: "USUARIO_DO_GRUPO", label: "Usuário do Grupo" },
    { value: "GRUPO_POR_CAMPO", label: "Grupo por campo do formulário" },
    { value: "GRUPO_DO_TIPO_PRODUTO", label: "Grupo do Tipo de Produto (Compras)" },
    { value: "GRUPO_DA_FILIAL", label: "Grupo da Filial" },
];

const PROCESSO_OPTIONS = Object.entries(PROCESSO_REGISTRY).map(([value, info]) => ({ value, label: info.label }));

const inicial = {
    id: null,
    tipoProcesso: null,
    etapaCodigo: null,
    tipoAtribuicao: null,
    usuarioId: null,
    grupoId: null,
    campoCondicional: "",
    valorCondicional: "",
};

export default function ConfigAtribuicoes() {
    const { configs, usuarios, grupos, loading, saving, salvar, excluir } = useAtribuicoesConfig();
    const { etapas: etapasConfig, loading: loadingEtapasConfig, salvandoChave, alternarAnexoObrigatorio, salvarPrazoHoras } = useEtapasConfiguracao();
    const [search, setSearch] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [form, setForm] = useState(inicial);
    const [paraExcluir, setParaExcluir] = useState(null);
    const [grupoDetalhe, setGrupoDetalhe] = useState(null);

    useEffect(() => {
        document.title = "MyDash | Configuração de Atribuições";
    }, []);

    const etapasDoProcesso = form.tipoProcesso ? processoInfo(form.tipoProcesso)?.etapas || [] : [];

    const gruposEtapas = useMemo(() => Object.keys(PROCESSO_REGISTRY).map(tipoProcesso => ({
        tipoProcesso,
        label: processoInfo(tipoProcesso)?.label || tipoProcesso,
        itens: (processoInfo(tipoProcesso)?.etapas || []).map(e => {
            const cfg = etapasConfig.find(c => c.tipoProcesso === tipoProcesso && c.etapaCodigo === e.codigo);
            return {
                tipoProcesso,
                etapaCodigo: e.codigo,
                label: e.label,
                anexoObrigatorio: cfg?.anexoObrigatorio || false,
                prazoHoras: cfg?.prazoHoras ?? null,
            };
        }),
    })), [etapasConfig]);

    const configsFiltradas = useMemo(() => configs.filter(c =>
        !search ||
        c.tipoProcesso?.toLowerCase().includes(search.toLowerCase()) ||
        c.etapaCodigo?.toLowerCase().includes(search.toLowerCase()) ||
        c.grupoNome?.toLowerCase().includes(search.toLowerCase()) ||
        c.usuarioNome?.toLowerCase().includes(search.toLowerCase())
    ), [configs, search]);

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    function abrirModal(config = null) {
        if (config) {
            setForm({
                id: config.id,
                tipoProcesso: config.tipoProcesso,
                etapaCodigo: config.etapaCodigo,
                tipoAtribuicao: config.tipoAtribuicao,
                usuarioId: config.usuarioId,
                grupoId: config.grupoId,
                campoCondicional: config.campoCondicional || "",
                valorCondicional: config.valorCondicional || "",
            });
        } else {
            setForm(inicial);
        }
        setModalOpen(true);
    }

    function fecharModal() {
        setModalOpen(false);
    }

    function salvarForm(e) {
        e.preventDefault();
        const dados = {
            tipoProcesso: form.tipoProcesso,
            etapaCodigo: form.etapaCodigo,
            tipoAtribuicao: form.tipoAtribuicao,
            usuarioId: form.tipoAtribuicao === "USUARIO" ? form.usuarioId : null,
            grupoId: (form.tipoAtribuicao !== "USUARIO" && form.tipoAtribuicao !== "GRUPO_DO_TIPO_PRODUTO" && form.tipoAtribuicao !== "GRUPO_DA_FILIAL")
                ? form.grupoId : null,
            campoCondicional: form.tipoAtribuicao === "GRUPO_POR_CAMPO" ? form.campoCondicional : null,
            valorCondicional: form.tipoAtribuicao === "GRUPO_POR_CAMPO" ? form.valorCondicional : null,
        };
        salvar(form.id, dados).then(fecharModal).catch(() => { });
    }

    function abrirGrupoDetalhe(grupoId) {
        const grupo = grupos.find(g => g.id === grupoId);
        if (grupo) setGrupoDetalhe(grupo);
    }

    function confirmarExclusao(config) {
        setParaExcluir(config);
        setDeleteModalOpen(true);
    }

    function excluirConfirmado() {
        excluir(paraExcluir.id).then(() => setDeleteModalOpen(false)).catch(() => { });
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="mx-auto">
                <h1 className="font-display text-2xl font-bold text-foreground mb-4">Configuração de Atribuições</h1>

                <TabView>
                    <TabPanel header="Atribuição de Etapas">
                        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                            <div className="flex flex-col gap-4 bg-primary px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-7">
                                <h2 className="font-display text-lg font-bold text-primary-foreground">Atribuições das Etapas</h2>
                                <button
                                    onClick={() => abrirModal()}
                                    className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-sm font-semibold text-primary transition-colors duration-150 hover:bg-muted"
                                >
                                    <i className="fas fa-plus mr-2" /> Nova Atribuição
                                </button>
                            </div>

                            <div className="border-b border-border bg-muted px-5 py-5 md:px-7">
                                <div className="relative w-full xl:w-72">
                                    <input
                                        type="text"
                                        placeholder="Pesquisar..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-input rounded-lg transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                                    />
                                    <i className="fas fa-search absolute left-3 top-3 text-muted-foreground" />
                                </div>
                            </div>

                            {loading ? (
                                <Loading />
                            ) : (
                                <div className="border-b border-border">
                                    <div className="overflow-x-auto fade-in">
                                        <table className="min-w-full divide-y divide-border">
                                            <thead className="bg-table-header">
                                                <tr className="text-sm">
                                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Processo</th>
                                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Etapa</th>
                                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Tipo</th>
                                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Destino</th>
                                                    <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Condição</th>
                                                    <th className="py-3 px-6 text-right text-xs font-medium text-table-header-fg uppercase tracking-wider">Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border bg-card">
                                                {configsFiltradas.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="px-6 py-4 text-center text-muted-foreground">
                                                            Nenhuma atribuição configurada
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    configsFiltradas.map(c => (
                                                        <tr key={c.id} className="text-xs transition-colors duration-150 hover:bg-table-row-hover">
                                                            <td className="px-6 py-3 whitespace-nowrap font-medium text-foreground">
                                                                {processoInfo(c.tipoProcesso)?.label || c.tipoProcesso}
                                                            </td>
                                                            <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{c.etapaCodigo}</td>
                                                            <td className="px-6 py-3 whitespace-nowrap">
                                                                <span className="inline-flex rounded-md bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary">
                                                                    {TIPO_ATRIBUICAO_OPTIONS.find(o => o.value === c.tipoAtribuicao)?.label || c.tipoAtribuicao}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-3 whitespace-nowrap text-foreground">
                                                                {c.usuarioNome || (
                                                                    c.grupoNome ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => abrirGrupoDetalhe(c.grupoId)}
                                                                            className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
                                                                            title="Ver usuários do grupo"
                                                                        >
                                                                            <i className="fa fa-users text-[10px]" />
                                                                            {c.grupoNome}
                                                                        </button>
                                                                    ) : (
                                                                        c.tipoAtribuicao === "GRUPO_DO_TIPO_PRODUTO" ? "Automático (Tipo de Produto)"
                                                                            : c.tipoAtribuicao === "GRUPO_DA_FILIAL" ? "Automático (Filial)"
                                                                                : "-"
                                                                    )
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">
                                                                {c.campoCondicional ? `${c.campoCondicional} = ${c.valorCondicional}` : "-"}
                                                            </td>
                                                            <td className="px-3 py-3 whitespace-nowrap text-right">
                                                                <div className="flex justify-end gap-2">
                                                                    <i onClick={() => abrirModal(c)} className="fa fa-pencil-square text-primary hover:text-primary/80 p-1 rounded-full hover:bg-primary/10 cursor-pointer" />
                                                                    <i onClick={() => confirmarExclusao(c)} className="fa fa-times text-destructive hover:text-destructive/80 p-1 rounded-full hover:bg-destructive/10 cursor-pointer" />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </TabPanel>

                    <TabPanel header="Configuração das Etapas">
                        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                            <div className="bg-primary px-4 py-3 md:px-7">
                                <h2 className="font-display text-lg font-bold text-primary-foreground">Configuração das Etapas</h2>
                                <p className="text-xs text-primary-foreground/80 mt-0.5">
                                    Defina, por etapa, se é obrigatório anexar documento e o prazo (SLA) a partir do momento em que ela é atribuída. Deixe o prazo em branco para não ter prazo.
                                </p>
                            </div>

                            {loadingEtapasConfig ? (
                                <Loading />
                            ) : (
                                <div className="divide-y divide-border">
                                    {gruposEtapas.map(grupo => (
                                        <div key={grupo.tipoProcesso} className="px-5 py-4 md:px-7">
                                            <h3 className="text-sm font-semibold text-foreground mb-3">{grupo.label}</h3>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full">
                                                    <thead>
                                                        <tr className="text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                                            <th className="py-2 pr-4">Etapa</th>
                                                            <th className="py-2 px-4 text-center">Anexo obrigatório</th>
                                                            <th className="py-2 pl-4 text-right">Prazo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-border">
                                                        {grupo.itens.map(item => {
                                                            const chave = `${item.tipoProcesso}:${item.etapaCodigo}`;
                                                            const salvando = salvandoChave === chave;
                                                            return (
                                                                <tr key={chave} className="text-sm">
                                                                    <td className="py-2.5 pr-4 text-foreground">{item.label}</td>
                                                                    <td className="py-2.5 px-4">
                                                                        <div className="flex items-center justify-center gap-2">
                                                                            {salvando && <i className="fas fa-spinner fa-spin text-muted-foreground text-xs" />}
                                                                            <InputSwitch
                                                                                checked={item.anexoObrigatorio}
                                                                                disabled={salvando}
                                                                                onChange={() => alternarAnexoObrigatorio(item)}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-2.5 pl-4">
                                                                        <div className="flex justify-end">
                                                                            <PrazoHorasCampo
                                                                                item={item}
                                                                                salvando={salvando}
                                                                                onSalvar={horas => salvarPrazoHoras(item, horas)}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabPanel>

                    <TabPanel header="Filial × Aprovação">
                        <FilialAprovacaoConfigTab />
                    </TabPanel>

                    <TabPanel header="Configuração">
                        <ConfiguracaoGeralTab />
                    </TabPanel>
                </TabView>
            </div>

            <Dialog
                header={form.id ? "Atualizar Atribuição" : "Nova Atribuição"}
                onHide={fecharModal}
                visible={modalOpen}
                blockScroll
                className="w-[95%] max-w-2xl"
                footer={
                    <div className="flex justify-end space-x-3 mt-3">
                        <button type="button" onClick={fecharModal} className="px-4 py-2 bg-card border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                        <button
                            type="submit"
                            onClick={salvarForm}
                            disabled={saving}
                            className="px-4 py-2 bg-primary hover:brightness-95 text-primary-foreground rounded-md transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving && <i className="fas fa-spinner fa-spin" />}
                            {saving ? "Salvando..." : "Salvar"}
                        </button>
                    </div>
                }
            >
                <form className="p-4 flex flex-col gap-4" onSubmit={salvarForm}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Processo <span className="text-destructive">*</span></label>
                            <CustomDropdown
                                value={form.tipoProcesso}
                                onChange={e => setForm(prev => ({ ...prev, tipoProcesso: e.value, etapaCodigo: null }))}
                                options={PROCESSO_OPTIONS}
                                placeholder="Selecione"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Etapa <span className="text-destructive">*</span></label>
                            <CustomDropdown
                                value={form.etapaCodigo}
                                onChange={e => set("etapaCodigo", e.value)}
                                options={etapasDoProcesso}
                                optionValue="codigo"
                                optionLabel="label"
                                placeholder={form.tipoProcesso ? "Selecione" : "Escolha o processo primeiro"}
                                disabled={!form.tipoProcesso}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-foreground/80">Destino <span className="text-destructive">*</span></label>
                        <CustomDropdown
                            value={form.tipoAtribuicao}
                            onChange={e => set("tipoAtribuicao", e.value)}
                            options={TIPO_ATRIBUICAO_OPTIONS}
                            placeholder="Selecione o tipo de atribuição"
                        />
                    </div>

                    {form.tipoAtribuicao === "USUARIO" && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">Usuário <span className="text-destructive">*</span></label>
                            <CustomDropdown value={form.usuarioId} onChange={e => set("usuarioId", e.value)} options={usuarios} optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                        </div>
                    )}

                    {(form.tipoAtribuicao === "GRUPO" || form.tipoAtribuicao === "USUARIO_DO_GRUPO" || form.tipoAtribuicao === "GRUPO_POR_CAMPO") && (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">
                                Grupo <span className="text-destructive">*</span>
                                {form.tipoAtribuicao === "USUARIO_DO_GRUPO" && (
                                    <span className="text-muted-foreground font-normal ml-1">(quem executa a etapa anterior escolhe o membro)</span>
                                )}
                            </label>
                            <CustomDropdown value={form.grupoId} onChange={e => set("grupoId", e.value)} options={grupos} optionValue="id" optionLabel="nome" placeholder="Selecione" filter />
                        </div>
                    )}

                    {form.tipoAtribuicao === "GRUPO_POR_CAMPO" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                                label="Campo do formulário"
                                required
                                placeholder="Ex: filial"
                                value={form.campoCondicional}
                                onChange={e => set("campoCondicional", e.target.value)}
                                helpText="Nome do campo avaliado (ex.: filial)"
                            />
                            <Input
                                label="Valor esperado"
                                required
                                placeholder="Ex: TERESINA"
                                value={form.valorCondicional}
                                onChange={e => set("valorCondicional", e.target.value)}
                                helpText="Cadastre uma linha para cada valor possível"
                            />
                        </div>
                    )}
                </form>
            </Dialog>

            <Dialog
                header={grupoDetalhe ? `Usuários do grupo "${grupoDetalhe.nome}"` : "Usuários do grupo"}
                onHide={() => setGrupoDetalhe(null)}
                visible={!!grupoDetalhe}
                blockScroll
                className="w-[95%] max-w-lg"
            >
                <div className="p-4">
                    <div className="border border-border rounded-lg overflow-hidden shadow-xs">
                        <div className="max-h-72 overflow-y-auto">
                            <table className="min-w-full divide-y divide-border">
                                <thead className="bg-table-header sticky top-0">
                                    <tr>
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Nome</th>
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">E-mail</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border bg-card">
                                    {(grupoDetalhe?.usuarios || []).length === 0 ? (
                                        <tr>
                                            <td colSpan="2" className="px-3 py-3 text-xs text-center text-muted-foreground">Nenhum usuário neste grupo</td>
                                        </tr>
                                    ) : (
                                        grupoDetalhe.usuarios.map(u => (
                                            <tr key={u.id} className="text-xs hover:bg-table-row-hover">
                                                <td className="px-3 py-2 whitespace-nowrap font-medium text-foreground">{u.nome}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{u.email}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </Dialog>

            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card rounded-lg shadow-pop w-full max-w-md p-6">
                        <p className="text-foreground">Você confirma a exclusão desta atribuição?</p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button onClick={() => setDeleteModalOpen(false)} className="px-4 py-2 border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                            <button onClick={excluirConfirmado} className="px-4 py-2 rounded-md transition-all duration-150 bg-destructive text-destructive-foreground hover:brightness-95">Excluir</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Input numérico com estado local próprio (edita livre, só salva no blur) — evita disparar uma
// chamada a cada tecla digitada. Vazio = sem prazo configurado para a etapa.
function PrazoHorasCampo({ item, salvando, onSalvar }) {
    const [valor, setValor] = useState(item.prazoHoras ?? "");

    useEffect(() => {
        setValor(item.prazoHoras ?? "");
    }, [item.prazoHoras]);

    function salvar() {
        if (valor === "") {
            if (item.prazoHoras != null) onSalvar(null);
            return;
        }
        const numero = Number(valor);
        if (Number.isNaN(numero) || numero < 0) {
            setValor(item.prazoHoras ?? "");
            return;
        }
        if (numero !== item.prazoHoras) onSalvar(numero);
    }

    return (
        <div className="flex items-center gap-2 shrink-0">
            {salvando && <i className="fas fa-spinner fa-spin text-muted-foreground text-xs" />}
            <input
                type="number"
                min="0"
                step="0.5"
                placeholder="Sem prazo"
                value={valor}
                disabled={salvando}
                onChange={e => setValor(e.target.value)}
                onBlur={salvar}
                className="w-24 text-right px-2 py-1.5 border border-input rounded-md text-sm outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 disabled:opacity-60"
            />
            <span className="text-xs text-muted-foreground w-3">h</span>
        </div>
    );
}
